import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Row from '../../../../components/Row';
import Column from '../../../../components/Column';
import ConnectionSearchFilter from '../../../../fragments/ConnectionSearchFilter';
import FilterButton from '../../../../fragments/FilterButton';
import ConnectionFilterLabels from '../../../../fragments/ConnectionFilterLabels';
import QuickActions from '../connections/QuickActions';
import { SidebarTypes } from '../../../../interfaces/store/appStore';
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers';
import { selectConnectionsFilter, useConnectionsStore } from '../../../../stores/connections/useConnectionsStore';
import { useAuthStore } from '../../../../stores/auth/useAuthStore';
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import { useSaveFilterHandlers } from '../../../../services/actions/save-filters/useSaveFilterHandlers';

interface ICampaignContactsFilterBarProps {
  onFilterChange: () => void;
  /** Clear the contact search results table without triggering a new search. */
  onClearContactResults: () => void;
}

const CampaignContactsFilterBar: FunctionComponent<ICampaignContactsFilterBarProps> = ({ onFilterChange, onClearContactResults }) => {
  const { store } = useConnectionsStore();
  const connectionsFilter = selectConnectionsFilter(store);
  const { store: { user } } = useAuthStore();
  const { openSidebar, showErrorToast, showSuccessToast } = useAppHandlers();
  const {
    numberOfConnectionsFiltersHandler,
    setScopedConnectionsFilterHandler: setConnectionsFilterHandler,
    setClearScopedConnectionsFilterHandler: setClearConnectionsFilterHandler,
    getMultipleConditionsFiltersHandler,
  } = useConnectionsHandlers();
  const { getSavedFiltersHandler, removeFiltersHandler } = useSaveFilterHandlers();
  const [savedFilters, setSavedFilters] = useState<any[]>([]);
  const skipNextFilterChange = useRef(true);
  const skipSearchOnFilterChange = useRef(false);

  const getSavedFilters = async () => {
    const result = await getSavedFiltersHandler();
    setSavedFilters(result ?? []);
  };

  useEffect(() => {
    getSavedFilters();
  }, []);

  useEffect(() => {
    if (connectionsFilter.savedFilter !== undefined) {
      getSavedFilters();
    }
  }, [connectionsFilter.savedFilter]);

  useEffect(() => {
    if (skipNextFilterChange.current) {
      skipNextFilterChange.current = false;
      return;
    }
    if (skipSearchOnFilterChange.current) {
      skipSearchOnFilterChange.current = false;
      return;
    }
    onFilterChange();
  }, [connectionsFilter]);

  const clearResultsWithoutSearch = () => {
    skipSearchOnFilterChange.current = true;
    onClearContactResults();
  };

  const onSetSavedFilterHandler = (savedFilter: any) => {
    setConnectionsFilterHandler({ ...connectionsFilter, ...savedFilter.filters });
  };

  const onRemoveSavedFilterHandler = async (savedFilter: any) => {
    setSavedFilters((prev: any[]) => prev.filter(filter => filter._id !== savedFilter._id));
    const result = await removeFiltersHandler(savedFilter);
    if (result.success) {
      showSuccessToast({ message: result?.message });
    } else {
      showErrorToast({ message: result?.message });
    }
  };

  const resetFilterHandler = () => {
    clearResultsWithoutSearch();
    setClearConnectionsFilterHandler();
  };

  const handleTagFilter = (autotagsFilter: any, isAutotagsFilterOr: 'yes' | 'no', key: string) => {
    setConnectionsFilterHandler({
      ...connectionsFilter,
      selectedUsersMutual: [{
        _id: user._id,
        firstName: user?.firstName,
        lastName: user?.lastName,
      }],
      isAutotagsFilterOr,
      usersMutualCondition: 'Or',
      autotagsFilter,
      quickActions: key,
    });
  };

  return (
    <Column gap="8px">
      <Row gap="8px" alignItems style={{ flexWrap: 'wrap' }}>
        <Row>
          <ConnectionSearchFilter searchValue={connectionsFilter?.searchValues?.[0]?.searchValue} />
        </Row>
        <Divider />
        <Row>
          <FilterButton
            sidebar={SidebarTypes.CONNECTION_FILTER}
            savedFilters={savedFilters}
            numberOfFilters={numberOfConnectionsFiltersHandler()}
            onSetSavedFilter={onSetSavedFilterHandler}
            onRemoveSavedFilter={onRemoveSavedFilterHandler}
            resetFilter={resetFilterHandler}
            onBeforeOpen={() => getMultipleConditionsFiltersHandler()}
          />
        </Row>
      </Row>
      <Row>
        <ConnectionFilterLabels
          searchValues={connectionsFilter?.searchValues}
          connectionsFilter={connectionsFilter}
          resetFiltersHandler={resetFilterHandler}
          saveFiltersHandler={() => openSidebar(SidebarTypes.SAVE_FILTER)}
          onRemoveLabel={() => clearResultsWithoutSearch()}
        />
      </Row>
      <Row>
        <QuickActions handleTagFilter={handleTagFilter} />
      </Row>
    </Column>
  );
};

const Divider = styled.div`
  height: 18px;
  width: 2px;
  background: ${({ theme: { quinaryColor } }) => `${quinaryColor}`};

  @media (max-width: 575px) {
    display: none;
  }
`;

export default CampaignContactsFilterBar;
